import type { Breadcrumb } from '@/types';
import { AccountDetailsTabs } from '~/enums/accounts/account-details-tabs.enum';
import { useWalletsStore } from '~/stores/wallets/wallets-store';
import { useAccountDetailsStore } from '~/stores/accounts/account-details-store';

export const useBreadcrumbItems = () => {
  const t = useNuxtApp().$i18n.t;
  const { walletIntId } = storeToRefs(useWalletsStore());
  const { id, accountDisplayName } = storeToRefs(useAccountDetailsStore());
  const { transactionInfos, transactionP2PInfos } = storeToRefs(useTransactionsStore());

  return computed<Breadcrumb[]>(() => [
    {
      name: 'partner-home',
      label: t('siteMap.home'),
      to: { name: 'partner-home' },
      dataQa: 'DASHBOARD_HOME_TITLE'
    },
    {
      name: 'announcements',
      label: t('siteMap.announcements')
    },
    {
      name: 'partner-profile-settings',
      to: { name: 'partner-profile-settings' },
      label: t('siteMap.profileSettings')
    },
    {
      name: 'partner-accounts',
      label: t('siteMap.accounts'),
      to: { name: 'partner-accounts-all' },
      children: [
        {
          name: 'partner-accounts-pending',
          label: t('siteMap.preAccounts'),
          to: { name: 'partner-accounts-pending' }
        },
        {
          name: 'partner-accounts-all',
          label: t('siteMap.accountsAll'),
          to: { name: 'partner-accounts-all' }
        },
        {
          name: 'partner-accounts-id',
          label: accountDisplayName.value,
          to: { name: 'partner-accounts-id', params: { id: id.value || 0 } }
        },
        {
          name: 'partner-accounts-id-tab',
          label: accountDisplayName.value,
          to: {
            name: 'partner-accounts-id-tab',
            params: { id: id.value || 0, tab: AccountDetailsTabs.Overview }
          }
        }
      ]
    },
    {
      name: 'partner-wallets',
      label: t('siteMap.wallets'),
      to: { name: 'partner-wallets-all' },
      children: [
        {
          name: 'partner-wallets-all',
          label: t('siteMap.walletsAll'),
          to: { name: 'partner-wallets-all' },
          dataQa: 'WALLET_LIST_BAR_TITLE'
        },
        {
          name: 'partner-wallets-wallet-statement',
          label: t('siteMap.walletStatement'),
          to: { name: 'partner-wallets-wallet-statement' },
          dataQa: 'WALLET_STATEMENT_BAR_TITLE'
        },
        {
          name: 'partner-wallets-id-tab-pmtab',
          label: '',
          to: {
            name: 'partner-wallets-id-tab-pmtab',
            params: { tab: 'overview', id: walletIntId.value ?? 0, pmtab: '' }
          }
        }
      ]
    },
    {
      name: 'partner-transactions',
      label: t('siteMap.transactions'),
      to: { name: 'partner-transactions-all' },
      children: [
        {
          name: 'partner-transactions-all',
          label: t('siteMap.transactionsAll'),
          to: { name: 'partner-transactions-all' }
        },
        {
          name: 'partner-transactions-in-out',
          label: t('siteMap.transactionsInOut'),
          to: { name: 'partner-transactions-in-out' }
        },
        {
          name: 'partner-transactions-requests',
          label: t('siteMap.transactionsRequests'),
          to: { name: 'partner-transactions-requests' }
        },
        {
          name: 'partner-transactions-p2p',
          label: t('siteMap.transactionsP2P'),
          to: { name: 'partner-transactions-p2p' }
        },
        {
          name: 'partner-transactions-p2p-id-tab',
          label: t('transactions.common.transactionId', { id: transactionP2PInfos.value?.id }),
          to: {
            name: 'partner-transactions-p2p-id-tab',
            params: { id: transactionP2PInfos.value?.id ?? 0, tab: 'details' }
          }
        },
        {
          name: 'partner-transactions-id-tab',
          label: t('transactions.common.transactionId', { id: transactionInfos.value?.id }),
          to: {
            name: 'partner-transactions-id-tab',
            params: { id: transactionInfos.value?.id ?? 0, tab: 'details' }
          }
        }
      ]
    },
    {
      name: 'partner-my-wallet-tab',
      label: t('siteMap.myWallet'),
      to: { name: 'partner-my-wallet-tab', params: { tab: 'overview' } }
    },
    {
      name: 'partner-branding',
      label: t('siteMap.branding'),
      to: { name: 'partner-branding-brand-tab', params: { tab: 'about' } },
      children: [
        {
          name: 'partner-branding-brand-tab',
          label: t('siteMap.brand'),
          to: { name: 'partner-branding-brand-tab', params: { tab: 'about' } }
        }
      ]
    },
    {
      name: 'partner-reports',
      label: t('siteMap.reports'),
      to: { name: 'partner-reports' }
    },

    {
      name: 'partner-configuration',
      label: t('siteMap.configuration'),
      to: { name: 'partner-configuration-ip-address-whitelist' },
      children: [
        {
          name: 'partner-configuration-ip-address-whitelist',
          label: t('siteMap.ipAddressWhitelist'),
          to: { name: 'partner-configuration-ip-address-whitelist' }
        },
        {
          name: 'partner-configuration-users-tab',
          label: t('siteMap.users'),
          to: { name: 'partner-configuration-users-tab', params: { tab: 'users' } }
        },
        {
          name: 'partner-configuration-roles',
          label: t('siteMap.roles'),
          to: { name: 'partner-configuration-roles' }
        },
        {
          name: 'partner-configuration-2fa',
          label: t('siteMap.2fa'),
          to: { name: 'partner-configuration-2fa' }
        },
        {
          name: 'partner-configuration-approval-workflow',
          label: t('siteMap.approvalWorkflow'),
          to: { name: 'partner-configuration-approval-workflow' }
        }
      ]
    },
    {
      name: 'partner-developers',
      label: t('siteMap.developers'),
      to: { name: 'partner-developers-api-keys' },
      children: [
        {
          name: 'partner-developers-api-keys',
          label: t('siteMap.apiKeys'),
          to: { name: 'partner-developers-api-keys' }
        },
        {
          name: 'partner-developers-notifications-tab',
          label: t('siteMap.notifications'),
          to: { name: 'partner-developers-notifications-tab', params: { tab: 'logs' } }
        }
      ]
    }
  ]);
};
